import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AccountTeamUserService } from './account-team-user.service';
import {
  LoadAccountTeamUsers,
  SetAccountTeamUsers,
  SetAccountTeamApiError,
  InviteUser,
  UpdateOrganizationRoles,
  UpdateOrganizationRolesSuccess,
  UpdateOrganizationRolesError,
  RemoveUser,
  RemoveUserSuccess,
  InviteUserError,
  RemoveUserError,
  InviteUserSuccess,
  ResetCallStateInviteUser,
  ResetCallStateRemoveUser,
  ResetCallStateUpdateUserRole,
  ResendInvitation,
  ResendInvitationSucceeded,
  ResendInvitationErrored,
  RemovePendingUser,
  RemovePendingUserSucceeded,
  RemovePendingUserErrored,
  RemoveUserFromOrg,
  RemovePendingUserFromOrg,
} from './account-team-user.actions';
import {
  mergeMap,
  catchError,
  map,
  delay,
  withLatestFrom,
  filter,
  switchMap,
} from 'rxjs/operators';
import { of } from 'rxjs';
import { AccountTeamUserApiError, AccountTeamUserApiErrorTypes } from './account-team-user.errors';
import { Store } from '@ngrx/store';
import { AppStore } from '../store.model';
import { isCallStateRemoveUserPending, selectUserInEdit } from './account-team-user.selectors';
import { PopupNotificationType } from 'studio-components';
import { PopupNotificationEffectCreatorService } from 'src/app/shared/services/effect-creators/popup-notification-effect-creator.service';

@Injectable()
export class AccountTeamUserEffects {
  readonly changeCallStateDelay = 3000;

  loadOrganizationUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadAccountTeamUsers),
      mergeMap(() =>
        this.accountTeamUserService.loadAccountTeamUsers().pipe(
          map(({ users }) => SetAccountTeamUsers({ users })),
          catchError((error) =>
            of(
              SetAccountTeamApiError({
                error: new AccountTeamUserApiError(
                  AccountTeamUserApiErrorTypes.LoadAccountTeamUsersError,
                  error,
                ),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  updateOrganizationUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UpdateOrganizationRoles),
      mergeMap((action) =>
        this.accountTeamUserService
          .updateOrganizationRoles(action.userId, action.organizationRole)
          .pipe(
            map((user) => UpdateOrganizationRolesSuccess({ user })),
            catchError((error) =>
              of(
                UpdateOrganizationRolesError({
                  error: new AccountTeamUserApiError(
                    AccountTeamUserApiErrorTypes.UpdateOrganizationRolesError,
                    error,
                  ),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  updateOrganizationRolesSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UpdateOrganizationRolesSuccess),
      switchMap((updateOrganizationRolesSuccess) =>
        of(updateOrganizationRolesSuccess).pipe(
          delay(this.changeCallStateDelay),
          map(() => ResetCallStateUpdateUserRole()),
        ),
      ),
    ),
  );

  inviteUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InviteUser),
      mergeMap((action) =>
        this.accountTeamUserService.inviteUser(action.user).pipe(
          map((user) => InviteUserSuccess({ user })),
          catchError((error) => {
            if (error.status === 409) {
              return of(
                InviteUserError({
                  error: new AccountTeamUserApiError(
                    AccountTeamUserApiErrorTypes.ConflictUserError,
                    error,
                  ),
                }),
              );
            }

            return of(
              InviteUserError({
                error: new AccountTeamUserApiError(
                  AccountTeamUserApiErrorTypes.InviteUserError,
                  error,
                ),
              }),
            );
          }),
        ),
      ),
    ),
  );

  inviteUserSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InviteUserSuccess),
      delay(this.changeCallStateDelay),
      map(() => ResetCallStateInviteUser()),
    ),
  );

  // TODO - retire legacy code STUD-2700
  removeUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoveUser),
      withLatestFrom(this.store.select(selectUserInEdit)),
      mergeMap(([, userInEdit]) =>
        this.accountTeamUserService.removeUser(userInEdit.user_id).pipe(
          map(() => RemoveUserSuccess({ userId: userInEdit.user_id })),
          catchError((error) =>
            of(
              RemoveUserError({
                error: new AccountTeamUserApiError(
                  AccountTeamUserApiErrorTypes.RemoveUserError,
                  error,
                ),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  removeUserFromOrg$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoveUserFromOrg),
      withLatestFrom(this.store.select(selectUserInEdit)),
      map(([, userInEdit]) => userInEdit.user_id),
      mergeMap((userId) =>
        this.accountTeamUserService.removeUserFromOrg(userId).pipe(
          map(() => RemoveUserSuccess({ userId })),
          catchError((error) =>
            of(
              RemoveUserError({
                error: new AccountTeamUserApiError(
                  AccountTeamUserApiErrorTypes.RemoveUserError,
                  error,
                ),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  removeUserSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemoveUserSuccess),
      switchMap((removeUserSuccess) =>
        of(removeUserSuccess).pipe(
          delay(this.changeCallStateDelay),
          withLatestFrom(this.store.select(isCallStateRemoveUserPending)),
          filter(([isSuccess, isPending]) => isSuccess && !isPending),
          map(() => ResetCallStateRemoveUser()),
        ),
      ),
    ),
  );

  resendInvitation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResendInvitation),
      switchMap(({ user }) =>
        this.accountTeamUserService.resendInvitation(user.user_id).pipe(
          map(() => ResendInvitationSucceeded({ user })),
          catchError(() => of(ResendInvitationErrored({ user }))),
        ),
      ),
    ),
  );

  displayResendInvitationSuccessfulPopupNotification$ =
    this.popupNotificationEffectCreatorService.createEffect({
      initiator: ResendInvitationSucceeded,
      notificationType: PopupNotificationType.Info,
      title: () => 'Resend invitation',
      message: ({ user }) => `Invitation has been sent to ${user.name}.`,
    });

  displayResendInvitationFailedPopupNotification$ =
    this.popupNotificationEffectCreatorService.createEffect({
      initiator: ResendInvitationErrored,
      notificationType: PopupNotificationType.Error,
      title: () => 'Resend invitation',
      message: ({ user }) =>
        `Oops, failed to resend an invitation to ${user.name}. Please try again later.`,
    });

  // TODO - retire legacy code STUD-2700
  removePendingUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemovePendingUser),
      switchMap(({ user }) =>
        this.accountTeamUserService.removePendingUser(user.user_id).pipe(
          map(() => RemovePendingUserSucceeded({ user })),
          catchError(() => of(RemovePendingUserErrored({ user }))),
        ),
      ),
    ),
  );

  removePendingUserFromOrg$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemovePendingUserFromOrg),
      mergeMap(({ user }) =>
        this.accountTeamUserService.removeUserFromOrg(user.user_id).pipe(
          map(() => RemovePendingUserSucceeded({ user })),
          catchError(() => of(RemovePendingUserErrored({ user }))),
        ),
      ),
    ),
  );

  displayRemovePendingUserSuccessfulPopupNotification$ =
    this.popupNotificationEffectCreatorService.createEffect({
      initiator: RemovePendingUserSucceeded,
      notificationType: PopupNotificationType.Info,
      title: () => 'Remove pending member',
      message: ({ user }) => `Removed ${user.name}.`,
    });

  displayRemovePendingUserFailedPopupNotification$ =
    this.popupNotificationEffectCreatorService.createEffect({
      initiator: RemovePendingUserErrored,
      notificationType: PopupNotificationType.Error,
      title: () => 'Remove pending member',
      message: ({ user }) => `Oops, failed to remove ${user.name}. Please try again later.`,
    });

  constructor(
    private accountTeamUserService: AccountTeamUserService,
    private actions$: Actions,
    private store: Store<AppStore>,
    private popupNotificationEffectCreatorService: PopupNotificationEffectCreatorService,
  ) {}
}
